// Persona — the user-chosen view lens (EPD / Content Author / GTM). Drives
// two things:
//
// NOTE: the underlying string identifiers stay `epd` / `enablement` /
// `sales` — only the display labels below were renamed. Persisted personas
// (localStorage, analytics_events.persona) still key off the original
// identifiers.
//
//   1. Layout emphasis. Which fields get promoted / demoted on cards +
//      detail pages.
//   2. Effective role. Persona caps the effective auth role via
//      useEffectiveRole — a real editor picking Sales sees the sales UI
//      (no drafts, no editor tools, no forward dates). Persona can only
//      demote; it cannot grant access a user's real auth doesn't have.
//
// Server-side auth is still authoritative — a determined user can inspect
// editor-only responses in DevTools even from Sales persona. The point is
// that the app *behaves* as the chosen role, so there's one control users
// touch instead of two overlapping ones.
//
// Defaults from the user's real auth role on first load, then persists to
// localStorage. Switching is one click via the top-right PersonaSelector.
import {
  createContext,
  useContext,
  useMemo,
  useState,
} from "react";
import type { Persona } from "@release-hub/shared";
import { useUser } from "./useUser";

const STORAGE_KEY = "release_hub_persona";
/** Set to "1" once the user has explicitly chosen a persona (either
 *  through the first-visit PersonaPicker or the sidebar selector). Used
 *  to decide whether to show the onboarding picker. Separate from
 *  STORAGE_KEY so a stored default from a legacy migration doesn't
 *  count as an explicit choice. */
const PICKED_KEY = "release_hub_persona_picked";
const VALID: readonly Persona[] = ["epd", "enablement", "sales"];

/** Coerce legacy stored values from earlier naming iterations:
 *   - pre-merge: PMM + CS + Sales Engineering were separate ("pmm", "se", "cs")
 *   - post-merge naming iterations: "field" → "customer" → "enablement".
 *  All roll forward to `enablement` transparently on load. */
const LEGACY_TO_CURRENT: Record<string, Persona> = {
  se: "enablement",
  pmm: "enablement",
  cs: "enablement",
  field: "enablement",
  customer: "enablement",
};

export const PERSONA_LABEL: Record<Persona, string> = {
  epd: "EPD",
  enablement: "Content Author",
  sales: "GTM",
};

export const PERSONA_DESCRIPTION: Record<Persona, string> = {
  epd: "Everything: internal notes, drafts, projected dates, gaps.",
  enablement:
    "PMM, CS, and Sales Engineering — workspaces, gaps, and audience-friendly narratives.",
  sales: "Selling: notable launches only, no forward-looking dates.",
};

interface Ctx {
  persona: Persona;
  /** True once the user has explicitly picked a persona. False on first
   *  visit; drives whether App renders the PersonaPicker or the normal
   *  layout. Any call to setPersona() flips this to true. */
  hasPicked: boolean;
  setPersona: (p: Persona) => void;
}
const PersonaCtx = createContext<Ctx | null>(null);

/** Best default given a real auth role. Editors get EPD, sales get sales,
 *  everyone else (CS + Sales Engineering) lands on the Enablement lens. */
function defaultFromRole(role: string | undefined): Persona {
  if (role === "editor") return "epd";
  if (role === "sales") return "sales";
  return "enablement";
}

export function PersonaProvider({ children }: { children: React.ReactNode }) {
  const { user } = useUser();
  const [persona, setPersonaState] = useState<Persona>(() => {
    if (typeof window === "undefined") return "epd";
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && VALID.includes(stored as Persona)) return stored as Persona;
    if (stored && LEGACY_TO_CURRENT[stored]) {
      // One-shot upgrade of pre-merge stored values.
      const upgraded = LEGACY_TO_CURRENT[stored]!;
      try {
        localStorage.setItem(STORAGE_KEY, upgraded);
      } catch {
        /* ignore */
      }
      return upgraded;
    }
    return defaultFromRole(user?.role);
  });
  const [hasPicked, setHasPickedState] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(PICKED_KEY) === "1";
  });

  const value = useMemo<Ctx>(
    () => ({
      persona,
      hasPicked,
      setPersona: (p) => {
        setPersonaState(p);
        setHasPickedState(true);
        try {
          localStorage.setItem(STORAGE_KEY, p);
          localStorage.setItem(PICKED_KEY, "1");
        } catch {
          /* private mode etc — ignore */
        }
      },
    }),
    [persona, hasPicked],
  );

  return <PersonaCtx.Provider value={value}>{children}</PersonaCtx.Provider>;
}

export function usePersona() {
  const ctx = useContext(PersonaCtx);
  if (!ctx) throw new Error("usePersona outside PersonaProvider");
  return ctx;
}
